import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  Explore as ExploreIcon,
  Business as BusinessIcon,
  ConfirmationNumber as TicketIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import iwalkcvService, { IWDashboard } from '../../services/iwalkcv.service';

const IWalkCVDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<IWDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    iwalkcvService.getDashboard()
      .then(setDashboard)
      .catch(() => setError('Erro ao carregar dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const modules = [
    { title: 'Operadores', desc: 'Verificar e gerir operadores turísticos', icon: <BusinessIcon />, path: '/iwalkcv/operadores', color: '#0D7F9F' },
    { title: 'Experiências', desc: 'Moderar e publicar experiências', icon: <ExploreIcon />, path: '/iwalkcv/experiencias', color: '#6233A0' },
    { title: 'Reservas', desc: 'Ver todas as reservas e pagamentos', icon: <TicketIcon />, path: '/iwalkcv/reservas', color: '#10B981' },
  ];

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={1}>iWalkCV</Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Experiências Turísticas em Cabo Verde
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats */}
      {dashboard && (
        <Grid container spacing={2} mb={4}>
          {[
            { label: 'Experiências', value: dashboard.experiencias.total, sub: `${dashboard.experiencias.activas} ativas`, icon: <ExploreIcon />, color: '#0D7F9F' },
            { label: 'Operadores', value: dashboard.operadores.total, sub: `${dashboard.operadores.verificados} verificados`, icon: <CheckIcon />, color: '#6233A0' },
            { label: 'Reservas', value: dashboard.reservas.total, sub: `${dashboard.reservas.pagas} pagas`, icon: <TicketIcon />, color: '#10B981' },
            { label: 'Receita', value: `${Number(dashboard.reservas.receita).toLocaleString('pt-CV')} CVE`, sub: 'total gerado', icon: <TrendingUpIcon />, color: '#F59E0B' },
            { label: 'Avaliação', value: dashboard.avaliacoes.total > 0 ? Number(dashboard.avaliacoes.media).toFixed(1) : '—', sub: `${dashboard.avaliacoes.total} avaliações`, icon: <StarIcon />, color: '#EF4444' },
          ].map((stat) => (
            <Grid item xs={6} sm={4} md={2.4} key={stat.label}>
              <Paper sx={{ p: 2, borderRadius: 3, borderLeft: `4px solid ${stat.color}` }}>
                <Box display="flex" alignItems="center" gap={1} mb={1} color={stat.color}>{stat.icon}</Box>
                <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label} · {stat.sub}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Módulos */}
      <Grid container spacing={3}>
        {modules.map((mod) => (
          <Grid item xs={12} sm={4} key={mod.title}>
            <Card
              sx={{ cursor: 'pointer', borderRadius: 3, transition: 'transform .2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
              onClick={() => navigate(mod.path)}
            >
              <CardContent>
                <Box
                  sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: mod.color + '22', color: mod.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  {mod.icon}
                </Box>
                <Typography variant="h6" fontWeight={600}>{mod.title}</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>{mod.desc}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default IWalkCVDashboard;
